'use strict';

const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';

export default angular
  .module('main.notifications.groups.dataProvider', [])
  .factory('NotificationGroupsDataProvider', function(
    $q,
    $log,
    $rootScope,
    $state,
    $filter,
    utils,
    UsersService,
    Resource,
    NotificationService,
  ) {
    class NotificationGroupsDataProvider {

      // #region Resources

      /**
       * Current organization resource
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(() => {
        return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
      });

      /**
       * Organization notification groups resource
       * @type {Resource}
       */
      organizationNotificationGroupsResource = new Resource(() => {
        return NotificationService.getNotificationGroups(this.currentOrganizationResource.value.id)
          .then((data) => data.groups || []);
      }, [
        this.currentOrganizationResource
      ]);

      /**
       * Current (active) tab
       * @type {'users'|'settings'}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab
       * @return {'users'|'settings'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      // #endregion

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button
       */
      goBack() {
        $state.go('main.organizations.list');
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }
      }

      /**
       * Search users across entire cloud
       * @param {string} [email] Email address to search by.
       * @param {string} [phone] Phone number to search by.
       * @returns {Promise}
       */
      searchUsersGlobal(email, phone) {
        return UsersService.searchUsers(email, phone)
          .then(data => data);
      }

      /**
       * Create a notification group
       * @param {Object} group The notification group to update.
       * @returns {Promise}
       */
      createNotificationGroup(group) {
        return NotificationService.createOrUpdateNotificationGroup(this.currentOrganizationResource.value.id, group)
          .then(data => {
            return this.organizationNotificationGroupsResource.init(true)
              .then(() => data);
          });
      }

      /**
       * Update a notification group
       * @param {string} groupId The ID of the notification group to update.
       * @param {Object} groupData The data to update the notification group with.
       * @returns {Promise}
       */
      updateNotificationGroup(groupId, groupData) {
        const group = Object.assign({}, groupData, { groupId: groupId });
        return NotificationService.createOrUpdateNotificationGroup(this.currentOrganizationResource.value.id, group)
          .then(data => {
            return this.organizationNotificationGroupsResource.init(true)
              .then(() => data);
          });
      }

      /**
       * Delete a notification group
       * @param {string} groupId The ID of the notification group to delete.
       * @returns {Promise}
       */
      deleteNotificationGroup(groupId) {
        return NotificationService.deleteNotificationGroup(this.currentOrganizationResource.value.id, groupId)
          .then(data => {
            return this.organizationNotificationGroupsResource.init(true)
              .then(() => data);
          });
      }

      // #endregion

    }

    return NotificationGroupsDataProvider;
  })
  .name;
